import React, { useContext, useRef, useState } from 'react';

import {
  Divider,
  InputLabel,
  FormControl,
  MenuItem,
  Box,
  Fade,
  Paper,
  useTheme,
  MenuList,
  FormControlLabel,
  Button,
  Typography,
  Grid,
} from '@material-ui/core';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { MdOutlineShortText } from 'react-icons/md';
import { GrTextAlignLeft } from 'react-icons/gr';
import { IoMdRadioButtonOn } from 'react-icons/io';
import { IoCheckbox } from 'react-icons/io5';
import { MdLinearScale } from 'react-icons/md';
import { IoCalendarOutline } from 'react-icons/io5';
import { TbClockHour4 } from 'react-icons/tb';
import { VscClose } from 'react-icons/vsc';
import { RxHamburgerMenu } from 'react-icons/rx';
import { useDrop, useDrag } from 'react-dnd';
import { FiFileText } from 'react-icons/fi';
import { IoImageOutline } from 'react-icons/io5';
import { IoLink } from 'react-icons/io5';
import { IoCopyOutline } from 'react-icons/io5';
import { BsFillQuestionCircleFill } from 'react-icons/bs';
import { AiOutlinePlus } from 'react-icons/ai';
import { PiSignature } from 'react-icons/pi';
import { IoCloseOutline } from 'react-icons/io5';
import { TbFileTypePdf } from 'react-icons/tb';
import { v4 as uuidv4 } from 'uuid';

import { AppCheckbox, AppSelect, AppTextField } from '@vyce/core/src/components/inputs';
import { SimpleRichTextEditor } from '@vyce/core/src/components/inputs/SimpleRichTextEditor';
import { AppIconButton } from '@vyce/core/src/components/AppIconButton';
import { ItemTypes } from '@vyce/core/src/views/forms/constants';
import { GridActions } from '@vyce/core/src/components/GridActions';
import { ImageUploader } from '@vyce/core/src/components/ImageUploader';
import { Image } from '@vyce/core/src/types';
import { DeviceContext } from '@vyce/core/src/contexts';
import { compressFile } from '@vyce/core/src/components/ImageUploader/utils';

interface Props {
  questionIndex: number;
  handleRemove: () => void;
  questionId: string;
  findQuestion: (id: string) => { question: any; index: number };
  moveQuestion: (dragId: string, hoverIndex: number) => void;
  handleDuplicate: () => void;
  uploadFileToFirebaseStorage: (file: File) => Promise<string | null>;
}

export const answerTypes = [
  {
    label: 'Short text',
    value: 'short_text',
    icon: <MdOutlineShortText />,
  },
  {
    label: 'Long text',
    value: 'long_text',
    icon: <GrTextAlignLeft />,
  },
  {
    label: 'Single choice',
    value: 'single_choice',
    icon: <IoMdRadioButtonOn />,
  },
  {
    label: 'Multiple choice',
    value: 'multiple_choice',
    icon: <IoCheckbox />,
  },
  {
    label: 'Linear scale',
    value: 'linear_scale',
    icon: <MdLinearScale />,
  },
  {
    label: 'Date',
    value: 'date',
    icon: <IoCalendarOutline />,
  },
  {
    label: 'Time',
    value: 'time',
    icon: <TbClockHour4 />,
  },
  {
    label: 'Photo',
    value: 'photo',
    icon: <IoImageOutline />,
  },

  {
    label: 'No answer required',
    value: 'no_answer',
    icon: <IoCloseOutline />,
  },
];

export const FormQuestionStep: React.FC<Props> = ({
  questionIndex,
  handleRemove,
  questionId,
  findQuestion,
  moveQuestion,
  handleDuplicate,
  uploadFileToFirebaseStorage,
}) => {
  const { control, setValue } = useFormContext();
  const ref = useRef<HTMLDivElement>(null);
  const originalIndex = findQuestion(questionId).index;
  const theme = useTheme();
  const question = useWatch({ control, name: `questions[${questionIndex}]` });
  const [closeGridAction, setCloseGridAction] = useState<boolean>(false);
  const [showDescription, setShowDescription] = useState<boolean>(!!question?.description);
  const [showPhoto, setShowPhoto] = useState<boolean>(!!question?.photo);
  const [showPdf, setShowPdf] = useState<boolean>(!!question?.pdf);
  const [showLink, setShowLink] = useState<boolean>(!!question?.videoLink);
  const [showExplanation, setShowExplanation] = useState<boolean>(!!question?.explanation);
  const [showSignature, setShowSignature] = useState<boolean>(!!question?.signature);
  const [fileUploading, setFileUploading] = useState<boolean>(false);
  const [imageUploading, setImageUploading] = useState<boolean>(false);
  const { isDesktop } = useContext(DeviceContext);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.FORM_QUESTION,
      item: { id: questionId, originalIndex },
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        const { id: droppedId, originalIndex } = item;
        const didDrop = monitor.didDrop();
        if (!didDrop) {
          moveQuestion(droppedId, originalIndex);
        }
      },
    }),
    [questionId, originalIndex, moveQuestion]
  );

  const [, drop] = useDrop(
    () => ({
      accept: ItemTypes.FORM_QUESTION,
      hover({ id: draggedId }: { id: string }) {
        if (draggedId !== questionId) {
          const { index: overIndex } = findQuestion(questionId);
          moveQuestion(draggedId, overIndex);
        }
      },
    }),
    [findQuestion, moveQuestion]
  );

  drag(drop(ref));

  const opacity = isDragging ? 0.5 : 1;

  const resetCloseStatus = () => {
    setCloseGridAction(true);
    setTimeout(() => setCloseGridAction(false), 100);
  };

  const handleImageUpload = async (image: Image) => {
    setImageUploading(true);
    const compressedFile = await compressFile(image.file as File);
    const url = await uploadFileToFirebaseStorage(compressedFile);
    if (url) {
      setValue(`questions[${questionIndex}].photo`, { name: image.name, url });
    }
    setImageUploading(false);
  };

  const handlePdfUpload = async (pdf: Image) => {
    setFileUploading(true);
    const url = await uploadFileToFirebaseStorage(pdf.file as File);
    if (url) {
      setValue(`questions[${questionIndex}].pdf`, { name: pdf.name, url });
    }
    setFileUploading(false);
  };

  const handleAddOption = () => {
    const newOption = {
      id: uuidv4(),
      label: '',
      value: '',
    };
    setValue(`questions[${questionIndex}].answer.options`, [...question.answer.options, newOption]);
  };

  const handleRemoveOption = (optionId: string) => {
    const options = question.answer.options;
    const index = options.findIndex((option: any) => option.id === optionId);
    options.splice(index, 1);
    setValue(`questions[${questionIndex}].answer.options`, options);
  };

  const handleAddSignature = () => {
    setShowSignature(true);
    setValue(`questions[${questionIndex}].signature`, true);
    resetCloseStatus();
  };

  const handleRemoveSignature = () => {
    setShowSignature(false);
    setValue(`questions[${questionIndex}].signature`, false);
  };

  const handleAddPdf = () => {
    setShowPdf(true);
    setValue(`questions[${questionIndex}].pdf`, null);
    resetCloseStatus();
  };

  return (
    <Fade in={true} timeout={200}>
      <div ref={ref} style={{ opacity }}>
        <Paper
          variant="outlined"
          style={{
            padding: 24,
            paddingLeft: 8,
            paddingTop: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <RxHamburgerMenu
            size={20}
            color={theme.palette.text.secondary}
            style={{ marginTop: 16, cursor: 'grab' }}
          />

          <Box style={{ width: '100%' }} display="flex" flexDirection="column" gridGap={16}>
            <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 16 }}>
              <Controller
                control={control}
                rules={{ required: true }}
                name={`questions[${questionIndex}].title`}
                render={({ field, formState }) => (
                  <AppTextField
                    {...field}
                    error={!!(formState.errors?.questions as any)?.[questionIndex]?.title}
                    value={field.value || ''}
                    label="Question title"
                    margin="normal"
                    fullWidth
                  />
                )}
              />

              <Box style={{ marginTop: 8 }} display="flex" alignItems="center" gridGap={16}>
                <GridActions close={closeGridAction}>
                  <MenuList>
                    <MenuItem
                      onClick={() => {
                        setShowDescription(true);
                        resetCloseStatus();
                      }}
                    >
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <FiFileText size={18} />
                        Add description
                      </Box>
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        setShowPhoto(true);
                        resetCloseStatus();
                      }}
                    >
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <IoImageOutline size={18} />
                        Add photo
                      </Box>
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        setShowLink(true);
                        resetCloseStatus();
                      }}
                    >
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <IoLink size={18} />
                        Add link to video
                      </Box>
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        setShowExplanation(true);
                        resetCloseStatus();
                      }}
                    >
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <BsFillQuestionCircleFill size={17} />
                        Add explanation
                      </Box>
                    </MenuItem>

                    <MenuItem onClick={handleAddPdf}>
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <TbFileTypePdf size={17} />
                        Add PDF document
                      </Box>
                    </MenuItem>

                    <MenuItem onClick={handleAddSignature}>
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <PiSignature size={17} />
                        Require signature
                      </Box>
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        handleDuplicate();
                        resetCloseStatus();
                      }}
                    >
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <IoCopyOutline size={17} />
                        Duplicate
                      </Box>
                    </MenuItem>
                  </MenuList>
                </GridActions>

                <AppIconButton onClick={handleRemove} disableFocusRipple disableRipple disableTouchRipple>
                  <VscClose size="20px" />
                </AppIconButton>
              </Box>
            </Box>

            {showSignature && (
              <Box display="flex" alignItems="center" gridGap={16} justifyContent="flex-end">
                <Typography variant="body2">
                  Worker's signature will be required to answer this question
                </Typography>

                <AppIconButton
                  onClick={() => handleRemoveSignature()}
                  disableFocusRipple
                  disableRipple
                  disableTouchRipple
                >
                  <VscClose size="20px" />
                </AppIconButton>
              </Box>
            )}

            {showDescription && (
              <Box display="flex" alignItems="flex-start" gridGap={16} marginTop={2}>
                <Controller
                  control={control}
                  name={`questions[${questionIndex}].description`}
                  render={({ field }) => (
                    <SimpleRichTextEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      label="Description"
                      fullWidth
                      rows={4}
                    />
                  )}
                />

                <Box>
                  <AppIconButton
                    onClick={() => setShowDescription(false)}
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                  >
                    <VscClose size="20px" />
                  </AppIconButton>
                </Box>
              </Box>
            )}

            {showPhoto && (
              <Box display="flex" alignItems="center" gridGap={16} position="relative">
                <ImageUploader
                  isDocument={true}
                  loading={imageUploading}
                  image={question.photo}
                  onImageUpload={handleImageUpload}
                  extraText="picture"
                  outsideControl
                  height={250}
                  width={250}
                />

                <Box style={{ position: 'absolute', top: 8, right: 8 }}>
                  <AppIconButton
                    onClick={() => setShowPhoto(false)}
                    disableFocusRipple
                    disableRipple
                    variant="error"
                    isSmall
                    disableTouchRipple
                  >
                    <VscClose size="20px" />
                  </AppIconButton>
                </Box>
              </Box>
            )}

            {showPdf && (
              <Box display="flex" alignItems="center" gridGap={16} position="relative">
                <ImageUploader
                  isDocument={true}
                  type="pdf"
                  loading={fileUploading}
                  image={question.pdf}
                  onImageUpload={handlePdfUpload}
                  extraText="PDF document"
                  outsideControl
                  height={200}
                  width={250}
                />

                <Box style={{ position: 'absolute', top: 8, right: 8 }}>
                  <AppIconButton
                    onClick={() => setShowPdf(false)}
                    disableFocusRipple
                    disableRipple
                    variant="error"
                    isSmall
                    disableTouchRipple
                  >
                    <VscClose size="20px" />
                  </AppIconButton>
                </Box>
              </Box>
            )}

            {showLink && (
              <Box display="flex" alignItems="center" gridGap={16}>
                <Controller
                  control={control}
                  name={`questions[${questionIndex}].videoLink`}
                  render={({ field }) => (
                    <AppTextField
                      {...field}
                      value={field.value || ''}
                      label="Link to YouTube video"
                      margin="normal"
                      fullWidth
                    />
                  )}
                />

                <Box style={{ marginTop: 8 }}>
                  <AppIconButton
                    onClick={() => setShowLink(false)}
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                  >
                    <VscClose size="20px" />
                  </AppIconButton>
                </Box>
              </Box>
            )}

            {(showPdf || showLink) && (
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <FormControlLabel
                  control={
                    <AppCheckbox
                      onChange={e => {
                        const checked = e.target.checked;
                        if (checked) {
                          setValue(`questions[${questionIndex}].blockNextQuestionSeconds`, 10);
                        } else {
                          setValue(`questions[${questionIndex}].blockNextQuestionSeconds`, null);
                        }
                      }}
                      color="primary"
                      checked={question.blockNextQuestionSeconds}
                    />
                  }
                  label="Block 'Next question' button for"
                  style={{ marginTop: 8 }}
                />

                <Controller
                  control={control}
                  name={`questions[${questionIndex}].blockNextQuestionSeconds`}
                  render={({ field }) => (
                    <AppTextField
                      {...field}
                      value={field.value || ''}
                      label="seconds"
                      margin="normal"
                      disabled={question.blockNextQuestionSeconds === null}
                    />
                  )}
                />
              </Box>
            )}

            {showExplanation && (
              <Box display="flex" alignItems="flex-start" gridGap={16} marginTop={2}>
                <Controller
                  control={control}
                  name={`questions[${questionIndex}].explanation`}
                  render={({ field }) => (
                    <SimpleRichTextEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      label="Explanation"
                      fullWidth
                      rows={4}
                    />
                  )}
                />

                <Box>
                  <AppIconButton
                    onClick={() => setShowExplanation(false)}
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                  >
                    <VscClose size="20px" />
                  </AppIconButton>
                </Box>
              </Box>
            )}

            <Divider style={{ margin: '8px 0 16px', height: '2px' }} />

            <Box>
              <Box display="flex" alignItems="center" gridGap={16}>
                <Controller
                  control={control}
                  name={`questions[${questionIndex}].answer.type`}
                  render={({ field }) => (
                    <FormControl style={{ flex: 1 }} variant="filled">
                      <InputLabel id="answerType">Answer type</InputLabel>
                      <AppSelect
                        onChange={e => field.onChange(e.target.value)}
                        value={field.value}
                        labelId="answerType"
                      >
                        {answerTypes.map(item => (
                          <MenuItem key={item.value} value={item.value}>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                              {item.icon} {item.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </AppSelect>
                    </FormControl>
                  )}
                />

                <Controller
                  control={control}
                  name={`questions[${questionIndex}].answer.required`}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <AppCheckbox onChange={field.onChange} color="primary" checked={field.value} />
                      }
                      label="Required"
                      style={{ marginTop: 8 }}
                    />
                  )}
                />
              </Box>

              {(question?.answer?.type === 'single_choice' ||
                question?.answer?.type === 'multiple_choice') && (
                <Box style={{ marginTop: 8 }}>
                  {question?.answer?.options?.map((option: any, i: number) => (
                    <Box display="flex" alignItems="center" gridGap={16}>
                      <AppCheckbox
                        variant={question?.answer?.type === 'single_choice' ? 'circle' : 'rectangle'}
                        style={{ marginTop: 8 }}
                        size="medium"
                        disabled={true}
                      />
                      <Controller
                        control={control}
                        name={`questions[${questionIndex}].answer.options[${i}].label`}
                        render={({ field }) => (
                          <AppTextField {...field} label="Option" margin="normal" fullWidth />
                        )}
                      />

                      <Box style={{ marginTop: 8 }}>
                        <AppIconButton
                          onClick={() => handleRemoveOption(option.id)}
                          disableFocusRipple
                          disableRipple
                          disableTouchRipple
                        >
                          <VscClose size="20px" />
                        </AppIconButton>
                      </Box>
                    </Box>
                  ))}

                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<AiOutlinePlus />}
                    onClick={handleAddOption}
                  >
                    Add option
                  </Button>
                </Box>
              )}

              {question?.answer?.type === 'linear_scale' && (
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gridGap={16}>
                      <Box style={{ minWidth: 80 }}>
                        <Controller
                          control={control}
                          name={`questions[${questionIndex}].answer.min`}
                          render={({ field }) => (
                            <AppTextField
                              label="Min"
                              {...field}
                              type="number"
                              margin="normal"
                              inputProps={{ min: 0 }}
                              onBlur={e => {
                                const value = Math.max(0, parseInt(e.target.value) || 0);
                                field.onChange(value);
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Controller
                        control={control}
                        name={`questions[${questionIndex}].answer.minLabel`}
                        render={({ field }) => (
                          <AppTextField {...field} label="Label (optional)" margin="normal" fullWidth />
                        )}
                      />

                      {isDesktop && (
                        <Box style={{ marginTop: 8, marginRight: 8 }}>
                          <Typography variant="body2">-</Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gridGap={16}>
                      <Box style={{ minWidth: 80 }}>
                        <Controller
                          control={control}
                          name={`questions[${questionIndex}].answer.max`}
                          render={({ field }) => (
                            <AppTextField
                              label="Max"
                              {...field}
                              type="number"
                              margin="normal"
                              inputProps={{ max: 10 }}
                              onBlur={e => {
                                const value = Math.min(10, Math.max(0, parseInt(e.target.value) || 0));
                                field.onChange(value);
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Controller
                        control={control}
                        name={`questions[${questionIndex}].answer.maxLabel`}
                        render={({ field }) => (
                          <AppTextField {...field} label="Label (optional)" margin="normal" fullWidth />
                        )}
                      />
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>
        </Paper>
      </div>
    </Fade>
  );
};
